import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * OA-471 §0/T1 — `useCentsField`: shared dollar-input ⇄ cents-storage state +
 * decimal-string parsing. Replaces the ad-hoc `Math.round(parseFloat(...) * 100)`
 * pattern duplicated per-field in `NoteSiloDetail.jsx` (that shape silently rounds
 * away extra precision and drifts on float edge cases — this hook does neither).
 *
 * PURE by design: this hook does ONLY cents⇄dollar conversion state and parsing.
 * It does NOT import `useDemo()` and knows nothing about demo mode. Demo-aware
 * DISPLAY formatting is a separate concern composed by the call site (a future
 * `<MoneyField>` wrapper), around this hook's raw value — never merged in here.
 *
 * Two exports beyond the hook:
 *  - `parseDollarInput(raw)` — the pure parser, usable imperatively (e.g. by
 *    `saveScalarField(field, rawValue)`) without instantiating the hook.
 *  - `centsToDollarString(cents)` — inverse, for seeding the edit input.
 */

export const MODE_SCALAR = "scalar"; // blank input = "no change, do not submit this field"
export const MODE_REQUIRED = "required"; // blank input = validation error, submit blocked

/**
 * Convert an integer cents value to an editable dollar string ("123456" → "1234.56").
 * `null`/`undefined` → "" (blank), so an unset field seeds an empty input, never "0.00".
 * Uses integer arithmetic only — no float intermediate, no drift.
 */
export function centsToDollarString(cents) {
  if (cents == null) return "";
  // Defensive on sign: this hook's callers are all non-negative fields, but we
  // don't want a stray negative stored value to render as garbage.
  const neg = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const dollars = Math.trunc(abs / 100);
  const rem = abs % 100;
  return `${neg ? "-" : ""}${dollars}.${String(rem).padStart(2, "0")}`;
}

/**
 * The decimal-string parser (OA-471 T1, arena round 6 P1's unambiguous rule).
 *
 * Returns `{ cents, error, isBlank }`:
 *  - blank input          → { cents: null, error: null, isBlank: true }  (caller's mode decides)
 *  - valid 0-2 decimals   → { cents: <int>, error: null, isBlank: false }
 *  - anything else        → { cents: null, error: <message>, isBlank: false }
 *
 * Rules, no ambiguity left to resolve at submit time:
 *  - More than 2 fractional digits is REJECTED outright, NEVER rounded.
 *    "12.345" and "1.005" are rejects, not near-cent rounds.
 *  - Exactly 0/1/2 fractional digits convert via INTEGER STRING ARITHMETIC
 *    (concatenate integer digits with the fractional digits padded to 2), never
 *    a floating-point intermediate — "1234.56" → exactly 123456.
 *  - Non-numeric input is rejected, INCLUDING comma thousands-separators
 *    ("1,234.56" is a reject, not a value of 1).
 *  - Negative values are rejected — all of this hook's current callers are
 *    non-negative fields (assumption; revisit if a signed money field appears).
 */
export function parseDollarInput(raw) {
  const s = (raw == null ? "" : String(raw)).trim();

  if (s === "") return { cents: null, error: null, isBlank: true };

  // Never-negative fields: a leading minus is a hard reject, not a value.
  if (s.startsWith("-")) {
    return { cents: null, error: "Enter a positive amount.", isBlank: false };
  }

  // At most one decimal point. More than one "." means more than two parts.
  const parts = s.split(".");
  if (parts.length > 2) {
    return { cents: null, error: "Enter a valid dollar amount.", isBlank: false };
  }

  const intPart = parts[0];
  const fracPart = parts.length === 2 ? parts[1] : "";

  // >2 fractional digits: REJECT outright, never round.
  if (fracPart.length > 2) {
    return { cents: null, error: "Enter at most 2 decimal places.", isBlank: false };
  }

  // "." alone (both sides empty) is not a number.
  if (intPart === "" && fracPart === "") {
    return { cents: null, error: "Enter a valid dollar amount.", isBlank: false };
  }

  // Both sides must be pure digits. This is what rejects commas ("1,234"),
  // letters ("12abc"), currency symbols, "+", internal whitespace, and
  // scientific notation ("1e3") — anything that isn't a plain decimal number.
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) {
    return { cents: null, error: "Enter a valid dollar amount.", isBlank: false };
  }

  // Integer STRING arithmetic — no parseFloat, no float multiply. Concatenate
  // the integer digits with the fractional digits padded to exactly 2, then
  // parse the whole string as one base-10 integer.
  const intDigits = intPart === "" ? "0" : intPart;
  const fracDigits = fracPart.padEnd(2, "0");
  const cents = parseInt(`${intDigits}${fracDigits}`, 10);

  if (!Number.isSafeInteger(cents)) {
    return { cents: null, error: "Amount is too large.", isBlank: false };
  }

  return { cents, error: null, isBlank: false };
}

/**
 * @param {number|null} initialCents  stored value in integer cents (null = unset)
 * @param {object} [options]
 * @param {"scalar"|"required"} [options.mode="scalar"]
 *        scalar   — blank means "no change, do not submit this field" (15 of 16 §1
 *                   fields, plus every optional field on the history-row forms).
 *        required — blank blocks submit with a validation error (used ONLY by the
 *                   money field `assessed_value` on the tax-assessments form).
 * @param {string} [options.requiredMessage] message shown when required & blank.
 */
export default function useCentsField(initialCents, options = {}) {
  const mode = options.mode === MODE_REQUIRED ? MODE_REQUIRED : MODE_SCALAR;
  const requiredMessage = options.requiredMessage || "This field is required.";

  const [raw, setRaw] = useState(() => centsToDollarString(initialCents));
  // `dirty` = there is an active uncommitted edit in progress. It gates the
  // async resync below so a server refresh never clobbers what the user is typing.
  const [dirty, setDirty] = useState(false);
  const lastCentsRef = useRef(initialCents);

  // Async resync (OA-471 §0, Codex eng-lens finding #5). `asset` reloads
  // asynchronously after saves, route changes, and server-side recomputation.
  // When the stored-cents prop changes:
  //   - no active edit → adopt the new value (don't let a stale dollar string
  //     survive a server refresh);
  //   - active edit whose parsed value now MATCHES the server → the edit has
  //     been committed upstream, so drop `dirty` (so a LATER server change is
  //     no longer treated as a clobber-risk);
  //   - active edit that does NOT match → leave it alone (never clobber an
  //     in-progress edit).
  // The `initialCents === lastCentsRef.current` guard makes this a no-op on
  // every render where the prop hasn't actually changed (incl. every keystroke).
  useEffect(() => {
    if (initialCents === lastCentsRef.current) return;
    lastCentsRef.current = initialCents;
    if (!dirty) {
      setRaw(centsToDollarString(initialCents));
    } else if (parseDollarInput(raw).cents === initialCents) {
      setDirty(false);
    }
  }, [initialCents, dirty, raw]);

  const onChange = useCallback((next) => {
    setDirty(true);
    setRaw(next == null ? "" : String(next));
  }, []);

  // Explicit reset — e.g. a Cancel button, or to re-seed after a commit if the
  // caller prefers an imperative reset over the auto-commit detection above.
  const reset = useCallback((nextCents) => {
    const base = nextCents === undefined ? lastCentsRef.current : nextCents;
    lastCentsRef.current = base;
    setDirty(false);
    setRaw(centsToDollarString(base));
  }, []);

  const parsed = useMemo(() => parseDollarInput(raw), [raw]);

  // Mode decides what a blank input means. Everything else (garbage, >2 decimals,
  // negatives) is a hard parser error in BOTH modes.
  const error = useMemo(() => {
    if (parsed.error) return parsed.error;
    if (parsed.isBlank) return mode === MODE_REQUIRED ? requiredMessage : null;
    return null;
  }, [parsed, mode, requiredMessage]);

  // `shouldSubmit`: is there a valid, non-blank value to send?
  //   scalar mode + blank  → false ("no change, don't submit this field")
  //   required mode + blank → false (error is set; caller blocks submit)
  //   valid value          → true
  const shouldSubmit = !parsed.isBlank && !error && parsed.cents !== null;

  return {
    // For the <Input>:
    displayValue: raw,
    onChange,
    // For submit-time reads:
    parsedCents: parsed.cents, // integer cents, or null when blank/invalid
    error, // validation message, or null
    isBlank: parsed.isBlank,
    shouldSubmit,
    // State/controls:
    dirty,
    reset,
    setRaw, // escape hatch for callers that need direct control
    mode,
  };
}
